import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useCampaignScope } from '../model/campaign-scope';
import { useTheme } from '@/shared/lib/theme';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { METABASE_IFRAME_HEIGHT_CLASS } from '@/features/dashboard/model/metabase-embed-height';
import { withMetabaseTheme } from '@/features/dashboard/model/metabase-theme';
import { cn } from '@/shared/lib/cn';

export function CampaignResultsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { idPassbook, listPath, organizationName } = useCampaignScope();
  const { resolvedTheme } = useTheme();

  const { data, isLoading, error } = useQuery({
    queryKey: ['campaign-results', idPassbook, campaignId],
    queryFn: async () => {
      const res = await fetch(`/bff/metabase/campaign/${idPassbook}/${campaignId}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'Resultados indisponíveis');
      }
      return res.json() as Promise<{ iframeUrl: string }>;
    },
    enabled: Boolean(idPassbook && campaignId),
  });

  return (
    <>
      <PageHeader
        title="Resultados da campanha"
        hideMetaOnMobile
        description={organizationName}
        actions={
          <Button variant="secondary" asChild>
            <Link to={listPath}>
              <ArrowLeft className="size-4" /> Voltar
            </Link>
          </Button>
        }
      />
      <div className="glass rounded-2xl">
        {isLoading ? (
          <div
            className={cn(
              'flex items-center justify-center text-sm text-muted-foreground',
              METABASE_IFRAME_HEIGHT_CLASS,
            )}
          >
            Carregando resultados…
          </div>
        ) : error ? (
          <div className="flex h-48 items-center justify-center p-6 text-sm text-destructive">
            {(error as Error).message}
          </div>
        ) : data?.iframeUrl ? (
          <iframe
            title="Resultados da campanha"
            src={withMetabaseTheme(data.iframeUrl, resolvedTheme)}
            className={cn('w-full border-0', METABASE_IFRAME_HEIGHT_CLASS)}
          />
        ) : null}
      </div>
    </>
  );
}
